import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Button,
  Chip,
  Stack,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import { useProject } from '../contexts/ProjectContext';
import { getProjectTones, createToneOfVoice, updateToneOfVoice } from '../services/api';
import ToneOfVoiceDialog from '../components/ToneOfVoiceDialog';
import StarIcon from '@mui/icons-material/Star';
import StarOutlineIcon from '@mui/icons-material/StarOutline';

const VoiceStyle = () => {
  const { selectedProject } = useProject();
  const [tones, setTones] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTone, setEditingTone] = useState(null);

  const loadTones = async () => {
    if (!selectedProject) return;
    try {
      const data = await getProjectTones(selectedProject.id);
      setTones(data);
    } catch (error) {
      console.error('Error loading tones:', error);
    }
  };

  useEffect(() => {
    loadTones();
  }, [selectedProject]);

  const handleOpenDialog = (tone = null) => {
    setEditingTone(tone);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingTone(null);
  };

  const handleSubmit = async (formData) => {
    try {
      if (editingTone) {
        await updateToneOfVoice(selectedProject.id, editingTone.id, formData);
      } else {
        await createToneOfVoice(selectedProject.id, formData);
      }
      await loadTones();
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving tone:', error);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Voice Style
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          disabled={!selectedProject}
        >
          Create New Style
        </Button>
      </Box>

      <Paper sx={{ p: 2 }}>
        {!selectedProject ? (
          <Typography color="textSecondary">Please select a project to manage voice styles</Typography>
        ) : tones.length === 0 ? (
          <Typography color="textSecondary">No voice styles defined yet</Typography>
        ) : (
          <List>
            {tones.map((tone) => (
              <ListItem
                key={tone.id}
                divider
                sx={{
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {tone.is_default ? (
                        <StarIcon color="primary" fontSize="small" />
                      ) : (
                        <StarOutlineIcon color="disabled" fontSize="small" />
                      )}
                      <Typography>{tone.name}</Typography>
                    </Box>
                  }
                  secondary={
                    <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                      {tone.selected_traits.map((trait) => (
                        <Chip
                          key={trait.id}
                          label={trait.name}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                    </Stack>
                  }
                />
                <ListItemSecondaryAction>
                  <IconButton edge="end" onClick={() => handleOpenDialog(tone)}>
                    <EditIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}
      </Paper>

      <ToneOfVoiceDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        onSubmit={handleSubmit}
        initialData={editingTone}
      />
    </Box>
  );
};

export default VoiceStyle; 